import React, { useMemo, useState } from 'react'
import { withTranslation, useTranslation } from 'react-i18next'
import { gql, useQuery, useLazyQuery } from '@apollo/client'
import {
  Autocomplete,
  Box,
  Button,
  Container,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme
} from '@mui/material'
import Header from '../components/Headers/Header'
import CustomLoader from '../components/Loader/CustomLoader'
import DateRangeFilter from '../components/TableHeader/DateRangeFilter'
import useGlobalStyles from '../utils/globalStyles'
import {
  getStoreReport,
  getRiderReport,
  getRestaurantOptions,
  getRiderOptions,
  getCompanyConfiguration
} from '../apollo'
import { generateReportPdf } from '../utils/reportPdf'
import ScheduleReports from '../components/Reporting/ScheduleReports'
import PartialRefunds from '../components/Reporting/PartialRefunds'

const GET_RESTAURANTS = gql`
  ${getRestaurantOptions}
`
const GET_RIDERS = gql`
  ${getRiderOptions}
`
const STORE_REPORT = gql`
  ${getStoreReport}
`
const RIDER_REPORT = gql`
  ${getRiderReport}
`
const GET_CONFIGURATION = gql`
  ${getCompanyConfiguration}
`

// Every control in the toolbar is the same height and sits under a label of the
// same size, so the row reads as one aligned band rather than three. Defined at
// module scope: a component created during render remounts its children on each
// keystroke, which would drop focus out of the date inputs.
const Field = ({ label, labelSx, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    <Typography sx={labelSx}>{label}</Typography>
    {children}
  </Box>
)

const STORE = 'store'
const RIDER = 'rider'

// "YYYY-MM-DD" in UTC, matching what the date inputs and the API exchange.
const toInputDate = date => date.toISOString().slice(0, 10)

const monthRange = offset => {
  const now = new Date()
  const start = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1)
  )
  const end = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset + 1, 0)
  )
  return { start: toInputDate(start), end: toInputDate(end) }
}

const Reporting = () => {
  // Generating a one-off report and managing standing schedules are separate
  // jobs on the same data, so they get separate tabs rather than one long page.
  const [tab, setTab] = useState('generate')
  const theme = useTheme()
  const { t } = useTranslation()
  const globalClasses = useGlobalStyles()

  const [reportType, setReportType] = useState(STORE)
  const [subjectId, setSubjectId] = useState('')
  const thisMonth = useMemo(() => monthRange(0), [])
  const [startDate, setStartDate] = useState(thisMonth.start)
  const [endDate, setEndDate] = useState(thisMonth.end)

  const { data: restaurantData, loading: restaurantsLoading } = useQuery(
    GET_RESTAURANTS
  )
  const { data: riderData, loading: ridersLoading } = useQuery(GET_RIDERS)
  const { data: configurationData } = useQuery(GET_CONFIGURATION)

  const [runStoreReport, storeState] = useLazyQuery(STORE_REPORT, {
    fetchPolicy: 'network-only'
  })
  const [runRiderReport, riderState] = useLazyQuery(RIDER_REPORT, {
    fetchPolicy: 'network-only'
  })

  const isStore = reportType === STORE
  const activeState = isStore ? storeState : riderState
  const report = isStore
    ? activeState.data?.storeReport
    : activeState.data?.riderReport

  const subjects = isStore
    ? restaurantData?.restaurantOptions ?? []
    : riderData?.riderOptions ?? []
  const subjectsLoading = isStore ? restaurantsLoading : ridersLoading
  const selectedSubject = subjects.find(item => item._id === subjectId)

  const canGenerate = Boolean(subjectId && startDate && endDate)

  const onTypeChange = (_event, nextType) => {
    // Null arrives when the active button is clicked again; keep the selection.
    if (!nextType || nextType === reportType) return
    setReportType(nextType)
    setSubjectId('')
  }

  const applyMonth = offset => {
    const { start, end } = monthRange(offset)
    setStartDate(start)
    setEndDate(end)
  }

  const onGenerate = () => {
    if (!canGenerate) return
    const variables = { startDate, endDate }
    if (isStore)
      runStoreReport({ variables: { ...variables, restaurantId: subjectId } })
    else runRiderReport({ variables: { ...variables, riderId: subjectId } })
  }

  const [downloadError, setDownloadError] = useState('')

  const onDownload = () => {
    if (!report) return
    const name = report.recipient?.name || 'report'
    const safeName = name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()
    try {
      setDownloadError('')
      generateReportPdf(report, {
        subject: name,
        fileName: `${safeName}-${startDate}-to-${endDate}.pdf`
      })
    } catch (error) {
      // Without this the failure is invisible: the click simply does nothing.
      setDownloadError(error?.message || 'Could not generate the PDF.')
    }
  }

  const symbol = report?.currencySymbol || ''
  const money = amount => `${symbol}${(Number(amount) || 0).toFixed(2)}`

  // Shared by the totals rows so a label and its amount stay on one baseline.
  const rowSx = { display: 'flex', justifyContent: 'space-between', mb: 1 }
  const mutedSx = { fontSize: 12, color: theme.palette.text.disabled }

  const CONTROL_HEIGHT = 38

  const labelSx = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: theme.palette.text.disabled,
    mb: 0.75
  }

  const presetSx = {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'none',
    height: CONTROL_HEIGHT,
    minWidth: 'auto',
    px: 1.5,
    borderRadius: '6px',
    color: theme.palette.primary.main,
    '&:hover': { backgroundColor: theme.palette.background.primary }
  }

  return (
    <>
      <Header />
      <Container
        maxWidth={false}
        disableGutters
        className={globalClasses.flex}
        sx={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          px: 3,
          pb: 3
        }}>
        <Box sx={{ width: '100%', mb: 2 }}>
          <ToggleButtonGroup
            exclusive
            value={tab}
            onChange={(event, value) => value && setTab(value)}
            sx={{
              height: CONTROL_HEIGHT,
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                px: 2.5,
                color: theme.palette.text.disabled,
                borderColor: theme.palette.grey[300],
                backgroundColor: theme.palette.background.paper,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.common.white,
                  '&:hover': { backgroundColor: theme.palette.primary.main3 }
                }
              }
            }}>
            <ToggleButton value="generate">{t('GenerateReport')}</ToggleButton>
            <ToggleButton value="schedule">{t('ScheduleReports')}</ToggleButton>
            <ToggleButton value="refunds">{t('PartialRefunds')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {tab === 'schedule' && <ScheduleReports />}

        {tab === 'refunds' && (
          <PartialRefunds
            currencySymbol={
              configurationData?.configuration?.currencySymbol || '£'
            }
          />
        )}

        {tab === 'generate' && (
          <>
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                p: 3,
                mb: 3,
                borderRadius: 2,
                // Paper defaults to text.primary, which this theme sets to #fff.
                color: theme.palette.text.secondary
              }}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  mb: 2.5,
                  color: theme.palette.text.secondary
                }}>
                {t('Reporting')}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'flex-end',
                  gap: 2.5
                }}>
                <Field label={t('ReportType')} labelSx={labelSx}>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={reportType}
                    onChange={onTypeChange}
                    sx={{
                      height: CONTROL_HEIGHT,
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 600,
                        px: 2,
                        color: theme.palette.text.disabled,
                        borderColor: theme.palette.grey[300],
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.common.white,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.main3
                          }
                        }
                      }
                    }}>
                    <ToggleButton value={STORE}>
                      {t('StoreReport')}
                    </ToggleButton>
                    <ToggleButton value={RIDER}>
                      {t('RiderReport')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Field>

                <Field
                  label={isStore ? t('StoreCol') : t('Rider')}
                  labelSx={labelSx}>
                  <Autocomplete
                    size="small"
                    value={selectedSubject || null}
                    onChange={(event, newValue) => {
                      setSubjectId(newValue?._id || '')
                    }}
                    options={subjects}
                    loading={subjectsLoading}
                    getOptionLabel={option => option?.name || ''}
                    isOptionEqualToValue={(option, value) =>
                      option._id === value?._id
                    }
                    noOptionsText={t('NoRecordsFound')}
                    loadingText={t('LoadingDots')}
                    disableClearable={false}
                    sx={{
                      width: 240,
                      '& .MuiOutlinedInput-root': {
                        height: CONTROL_HEIGHT,
                        fontSize: 12,
                        borderRadius: '6px',
                        backgroundColor: theme.palette.background.primary,
                        paddingRight: '32px !important', // Space for the arrow
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.grey[300]
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main
                        }
                      },
                      '& .MuiInputBase-input': {
                        fontSize: 12,
                        color: subjectId
                          ? theme.palette.text.secondary
                          : theme.palette.text.disabled,
                        padding: '6px 12px !important',
                        height: 'auto !important'
                      },
                      '& .MuiAutocomplete-endAdornment': {
                        position: 'absolute',
                        right: '4px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        '& .MuiIconButton-root': {
                          padding: 2,
                          margin: 0
                        }
                      },
                      '& .MuiAutocomplete-popupIndicator': {
                        padding: 2,
                        margin: 0
                      },
                      '& .MuiAutocomplete-clearIndicator': {
                        padding: 2,
                        margin: 0
                      }
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={
                          isStore ? t('SelectStore') : t('SelectRider')
                        }
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          sx: {
                            height: CONTROL_HEIGHT,
                            fontSize: 12,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.grey[300]
                            }
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <MenuItem
                        {...props}
                        key={option._id}
                        value={option._id}
                        sx={{
                          fontSize: 12,
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover
                          }
                        }}>
                        {option.name}
                      </MenuItem>
                    )}
                    PaperComponent={({ children }) => (
                      <Paper
                        sx={{
                          maxHeight: 320,
                          overflow: 'auto',
                          mt: 0.5,
                          borderRadius: '6px',
                          boxShadow: theme.shadows[3]
                        }}>
                        {children}
                      </Paper>
                    )}
                  />
                </Field>

                <Field label={t('Period')} labelSx={labelSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DateRangeFilter
                      size="medium"
                      showClear={false}
                      startDate={startDate}
                      endDate={endDate}
                      onChange={(nextStart, nextEnd) => {
                        setStartDate(nextStart)
                        setEndDate(nextEnd)
                      }}
                    />
                    <Box
                      sx={{
                        width: '1px',
                        height: 20,
                        backgroundColor: theme.palette.grey[300],
                        mx: 0.5
                      }}
                    />
                    <Button
                      size="small"
                      onClick={() => applyMonth(0)}
                      sx={presetSx}>
                      {t('ThisMonth')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => applyMonth(-1)}
                      sx={presetSx}>
                      {t('LastMonth')}
                    </Button>
                  </Box>
                </Field>

                {/* Pushed to the trailing edge so the primary action is unambiguous. */}
                <Box sx={{ marginLeft: { xs: 0, lg: 'auto' } }}>
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={onGenerate}
                    disabled={!canGenerate || activeState.loading}
                    sx={{
                      textTransform: 'none',
                      fontSize: 12,
                      fontWeight: 600,
                      height: CONTROL_HEIGHT,
                      px: 3,
                      borderRadius: '6px',
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main3
                      }
                    }}>
                    {t('GenerateReport')}
                  </Button>
                </Box>
              </Box>

              {activeState.error && (
                <Typography
                  sx={{ mt: 2, fontSize: 12, color: theme.palette.error.main }}>
                  {activeState.error.message}
                </Typography>
              )}
            </Paper>

            {activeState.loading && <CustomLoader />}

            {report && !activeState.loading && (
              <Paper
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  p: 3,
                  mb: 4,
                  borderRadius: 2,
                  overflow: 'hidden',
                  // Paper defaults to text.primary, which this theme sets to #fff,
                  // so every Typography inside would otherwise be white on white.
                  color: theme.palette.text.secondary
                }}>
                {/* On-screen preview of exactly what the PDF will contain. */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2
                  }}>
                  <Box>
                    <Typography
                      sx={{ fontSize: 12, color: theme.palette.text.disabled }}>
                      {t('InvoiceNo')} {report.invoiceNumber}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        mt: 1,
                        color: theme.palette.text.secondary
                      }}>
                      {report.recipient?.name}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, color: theme.palette.text.disabled }}>
                      {report.recipient?.address}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: theme.palette.text.secondary
                      }}>
                      {report.issuer?.companyName || t('CompanyDetailsMissing')}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, color: theme.palette.text.disabled }}>
                      {report.issuer?.companyAddress}
                    </Typography>
                    {report.issuer?.vatNumber && (
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: theme.palette.text.disabled
                        }}>
                        VAT No {report.issuer.vatNumber}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    mt: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: theme.palette.text.secondary
                    }}>
                    {report.orders?.length || 0} {t('Orders')}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={onDownload}
                    sx={{
                      textTransform: 'none',
                      fontSize: 12,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main
                    }}>
                    {t('DownloadPDF')}
                  </Button>
                </Box>

                {downloadError && (
                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: 12,
                      textAlign: 'right',
                      color: theme.palette.error.main
                    }}>
                    {downloadError}
                  </Typography>
                )}

                {/* The table scrolls within the card so a wide row cannot stretch the
                page; the layout clips overflow rather than scrolling it. */}
                <Box sx={{ width: '100%', overflowX: 'auto', mt: 2 }}>
                  <Table
                    size="small"
                    sx={{
                      minWidth: 720,
                      // theme.palette.text.primary is #fff, which TableCell uses by
                      // default and which renders invisibly on the white sheet.
                      '& .MuiTableCell-root': {
                        fontSize: 11,
                        color: theme.palette.text.secondary,
                        borderColor: theme.palette.divider
                      }
                    }}>
                    <TableHead>
                      <TableRow>
                        {[
                          '#',
                          t('Date'),
                          t('OrderID'),
                          t('Type'),
                          t('Card'),
                          t('Refund'),
                          t('Total')
                        ].map((heading, index) => (
                          <TableCell
                            key={heading}
                            align={index > 3 ? 'right' : 'left'}
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: theme.palette.text.secondary
                            }}>
                            {heading}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.orders?.map((order, index) => (
                        <TableRow key={`${order.orderId}-${index}`}>
                          <TableCell sx={{ fontSize: 11 }}>
                            {index + 1}
                          </TableCell>
                          <TableCell sx={{ fontSize: 11 }}>
                            {new Date(order.date).toLocaleDateString('en-GB', {
                              timeZone: 'UTC'
                            })}
                          </TableCell>
                          <TableCell sx={{ fontSize: 11 }}>
                            {order.orderId}
                          </TableCell>
                          <TableCell sx={{ fontSize: 11 }}>
                            {order.type}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 11 }}>
                            {order.card ? money(order.card) : ''}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 11 }}>
                            {order.refund ? money(-order.refund) : ''}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 11 }}>
                            {money(order.total)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={4} />
                        <TableCell
                          align="right"
                          sx={{ fontSize: 11, fontWeight: 700 }}>
                          {money(report.cardTotal)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontSize: 11, fontWeight: 700 }}>
                          {report.refundTotal ? money(-report.refundTotal) : ''}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontSize: 11, fontWeight: 700 }}>
                          {money(report.ordersTotal)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>

                <Box sx={{ mt: 3, ml: 'auto', width: 260 }}>
                  {report.charges?.map(charge => (
                    <Box
                      key={charge.label}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1
                      }}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: theme.palette.text.disabled
                        }}>
                        {charge.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: theme.palette.text.secondary
                        }}>
                        {money(charge.amount)}
                      </Typography>
                    </Box>
                  ))}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderTop: `1px solid ${theme.palette.divider}`,
                      pt: 1
                    }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                      {t('TotalIncVAT')}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                      {money(report.totalIncVat)}
                    </Typography>
                  </Box>

                  {/* What the store is owed. Runs from the gross charge, not from
                  item sales: the fee below includes delivery, service fees and
                  tips, which were never part of that sales figure. */}
                  {report.settlement && report.platformCollected && (
                    <Box sx={{ mt: 3 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1 }}>
                        {t('PaymentToStore')}
                      </Typography>
                      <Box sx={rowSx}>
                        <Typography sx={mutedSx}>
                          {t('TotalChargedToCustomers')}
                        </Typography>
                        <Typography sx={{ fontSize: 12 }}>
                          {money(report.platformCollected.chargedTotal)}
                        </Typography>
                      </Box>
                      {/* One line, matching the single application fee the store
                      sees on its Stripe statement. The note says what it covers. */}
                      <Box sx={rowSx}>
                        <Typography sx={mutedSx}>
                          {t('LessBorkuFee')}
                        </Typography>
                        <Typography sx={{ fontSize: 12 }}>
                          {money(
                            -Math.abs(report.platformCollected.total || 0)
                          )}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          borderTop: `1px solid ${theme.palette.divider}`,
                          pt: 1
                        }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                          {t('YourSales')}
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                          {money(report.settlement.payoutTotal)}
                        </Typography>
                      </Box>
                      {/* Credit-paid orders take no card payment, so this part of
                      the payout cannot come through Stripe. */}
                      {report.settlement.borkuToSettle > 0 && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 1,
                            pt: 1,
                            borderTop: `1px solid ${theme.palette.divider}`
                          }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                            {t('BorkuToSettle')}
                          </Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                            {money(report.settlement.borkuToSettle)}
                          </Typography>
                        </Box>
                      )}
                      <Typography
                        sx={{
                          fontSize: 10,
                          mt: 1,
                          color: theme.palette.text.disabled
                        }}>
                        {t('BorkuFeeCovers')}
                        {report.platformCollected.creditApplied > 0 &&
                          ` ${t('LessCreditsApplied')} ${money(
                            report.platformCollected.creditApplied
                          )}.`}
                      </Typography>
                      {report.settlement.borkuToSettle > 0 && (
                        <Typography
                          sx={{
                            fontSize: 10,
                            mt: 0.5,
                            color: theme.palette.text.disabled
                          }}>
                          {t('BorkuToSettleHelp')}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

            {selectedSubject &&
              !report &&
              !activeState.loading &&
              !activeState.error && (
                <Typography
                  sx={{
                    fontSize: 12,
                    color: theme.palette.text.disabled,
                    mb: 4
                  }}>
                  {t('GenerateReportHint')}
                </Typography>
              )}
          </>
        )}
      </Container>
    </>
  )
}

export default withTranslation()(Reporting)
